import {
  DATA_TYPES,
  FARMING_TYPES,
  FIELD_TYPES,
  IN_HOUSE_DATA_TYPES,
  INGREDIENT_TYPE,
  UNIT_TYPES
} from "../../../../config/constants";
import { IngredientDataTypeLabel } from "../../../../config/enums/IngredientType.enum";
import { SupplierType } from "../../../../config/enums/SupplierType.enum";
import { SelectOption } from "../types/ingredientRegisterView.types";

interface LegacyOption {
  key: number | string;
  text: string;
  value: string | number;
}

/**
 * Static-method dropdown-option mapping for the register form's Tailwind
 * `Select`s, single-responsibility (option shaping only, no state/API calls)
 * — mirrors `services/IngredientRowMapper.ts`'s convention. Reads the exact
 * same constants the legacy `SupplierIngredientDataView.tsx` uses, so the
 * option lists stay identical; only the on-screen control changed.
 */
export class IngredientRegisterOptions {
  private static toSelectOptions(options: LegacyOption[]): SelectOption[] {
    return options.map(option => ({ value: String(option.value), text: option.text }));
  }

  static unitTypes(): SelectOption[] {
    return this.toSelectOptions(UNIT_TYPES);
  }

  static ingredientTypes(): SelectOption[] {
    return this.toSelectOptions(INGREDIENT_TYPE);
  }

  static farmingTypes(): SelectOption[] {
    return this.toSelectOptions(FARMING_TYPES);
  }

  static fieldTypes(): SelectOption[] {
    return this.toSelectOptions(FIELD_TYPES);
  }

  /**
   * Data Type options depend on the selected supplier's type — in-house
   * suppliers can only be Generic/User, matching
   * `SupplerIngredientDataView.tsx`'s `DataTypes` gotcha exactly. Neither
   * legacy list includes `CALCULATED_LCA` (it isn't meant to be picked from
   * this dropdown — it's set once an ingredient's LCA data is calculated
   * elsewhere), but it's a real, persisted value here. The legacy Semantic
   * UI `Dropdown` could still show an unlisted value; the Tailwind `Select`
   * can't — it only renders something for a value that matches one of its
   * `SelectItem`s, so a `CALCULATED_LCA` record rendered blank. Appending
   * the current value as a (non-editable-elsewhere) option when it isn't
   * already in the list keeps that same "not normally selectable" business
   * rule while letting the field actually display it.
   */
  static dataTypes(supplierType?: SupplierType, currentValue?: string): SelectOption[] {
    const source = supplierType === SupplierType.Internal ? IN_HOUSE_DATA_TYPES : DATA_TYPES;
    const options = this.toSelectOptions(source);
    if (currentValue && !options.some(option => option.value === currentValue)) {
      const label = IngredientDataTypeLabel[currentValue as keyof typeof IngredientDataTypeLabel];
      options.push({ value: currentValue, text: label || currentValue });
    }
    return options;
  }

  static countries(countriesData?: Array<{ id: number; country: string }>): SelectOption[] {
    return (countriesData || []).map(country => ({
      value: String(country.id),
      text: country.country
    }));
  }

  static produceTypes(produceTypesData?: Array<{ id: number; produce_name: string }>): SelectOption[] {
    return (produceTypesData || []).map(produceType => ({
      value: String(produceType.id),
      text: produceType.produce_name
    }));
  }

  static specifyIngredients(
    specifyIngredientData?: Array<{ key: number | string; text: string; value: number | string }>
  ): SelectOption[] {
    return this.toSelectOptions(specifyIngredientData || []);
  }
}
